import * as coam from '@cimpress-technology/coam-sapidus'
import { SelectWrapper } from '@cimpress/react-components'
import styles from '@cimpress/react-components/lib/Select/styles'
import AsyncSelect from 'react-select/async'
import debounce from 'awesome-debounce-promise'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import auth from '../../common/auth'
import { useIsMounted } from '../../common/hooks/useIsMounted'
import { Disabled } from '../../common/models'
import { CanonicalCoamGroupMember, CanonicalCoamUser } from '../models'
import { UserProfile } from './UserList'

interface Props {
  existingMembers: CanonicalCoamGroupMember[]
  onSelect(principal: CanonicalCoamUser): void
}
interface Option extends Disabled {
  label: string
  value: string
  isClient: boolean
  user: CanonicalCoamUser
}

export default function UserLookup(props: Props) {
  const isMounted = useIsMounted()
  const { t } = useTranslation()

  const onChange = (data: Option) => {
    props.onSelect(data.user)
  }

  const findPrincipals = async (input: string) => {
    if (!isMounted.current) {
      return
    }
    const result = await coam.users.findCanonicalUsers(
      input,
      auth.getAccessToken(),
      auth.getProfile().sub,
      true
    )

    const rawOptions: Option[] = result.map(user => ({
      label: user.canonical_principal,
      value: user.canonical_principal,
      isClient: user.is_client,
      isDisabled: props.existingMembers.some(member => {
        return member.principal === user.canonical_principal
      }),
      user,
    }))

    const [alreadyAMember, areClients] = rawOptions.reduce(
      (accum, option) => {
        if (option.isDisabled) {
          accum[0].push(option)
        }
        if (option.isClient && !option.isDisabled) {
          accum[1].push(option)
        }

        return accum
      },
      [[] as Option[], [] as Option[]]
    )

    return alreadyAMember.length === 0 && areClients.length === 0
      ? rawOptions
      : [
          {
            label: t('userManagement.matchingUsers'),
            options: rawOptions.filter(
              option => !option.isDisabled && !option.isClient
            ),
          },
          {
            label: t('userManagement.matchingClients'),
            options: areClients,
          },
          {
            label: t('userManagement.alreadyAMember'),
            options: alreadyAMember,
          },
        ]
  }

  const debouncedFindPrincipals = debounce(findPrincipals, 300)

  const noOptionsMessage = (obj: { inputValue: string }) => {
    return ''
  }

  return (
    <SelectWrapper
      styles={styles}
      selectedSelect={AsyncSelect}
      loadOptions={debouncedFindPrincipals}
      cacheOptions={false}
      defaultOptions={false}
      isClearable={true}
      label={t('userManagement.searchForUser')}
      noOptionsMessage={noOptionsMessage}
      arrowRenderer={arrowRenderer}
      formatOptionLabel={formatOptionLabel}
      onChange={onChange}
      placeholder=""
    />
  )
}

function formatOptionLabel(option: Option, labelMeta: any) {
  return <UserProfile user={option.user} clickable={true} />
}

const arrowRenderer = () => <></>
